import { useRoute, useRouter, type Router, type RouteLocationNormalized } from "vue-router";
// From the leaf module, not the `job` barrel: that barrel pulls the whole API surface in behind
// one string.
import { TREATMENT_PLAN_JOB_ORIGIN } from "@/api/models/treatmentPlan/jobConstants";
import type { RouteLocationRaw } from "vue-router";

/**
 * Whether any component in this page load has written a job_id itself.
 *
 * A job_id in the URL has two very different origins: one a live component just wrote while
 * starting a job, and one left over in the address bar from a previous page load. They look
 * identical in the query string, so cleanup that only inspects the URL cannot tell them apart
 * and will happily wipe a param another component is still relying on. This flag records the
 * difference — it is set by every write below and survives until the next full page load.
 */
let jobIdWrittenThisSession = false;

/** True once any component has written a job_id during this page load. See the flag above. */
export function hasJobIdBeenWrittenThisSession(): boolean {
  return jobIdWrittenThisSession;
}

/** Test-only reset; the flag is otherwise cleared solely by a real page load. */
export function resetJobIdSessionOwnership(): void {
  jobIdWrittenThisSession = false;
}

/**
 * Get job_id from URL query parameters
 */
export function getJobIdFromUrl(route?: RouteLocationNormalized): string | null {
  const currentRoute = route ?? useRoute();
  const jobId = currentRoute.query[TREATMENT_PLAN_JOB_ORIGIN];
  return typeof jobId === "string" ? jobId : null;
}

/**
 * Add job_id to route location query parameters
 */
export function addJobIdToRoute(
  location: RouteLocationRaw | string | any,
  jobId?: string | null,
  route?: RouteLocationNormalized
): RouteLocationRaw {
  const currentJobId = jobId ?? getJobIdFromUrl(route);

  if (!currentJobId) {
    return location as RouteLocationRaw;
  }

  if (typeof location === "string") {
    return {
      path: location,
      query: { [TREATMENT_PLAN_JOB_ORIGIN]: currentJobId },
    };
  }

  if (location && typeof location === "object" && "path" in location) {
    return {
      ...location,
      query: {
        ...location.query,
        [TREATMENT_PLAN_JOB_ORIGIN]: currentJobId,
      },
    } as RouteLocationRaw;
  }

  return location as RouteLocationRaw;
}

/**
 * Update current URL with job_id query parameter
 */
export async function updateUrlWithJobId(
  jobId: string | null,
  router?: Router,
  route?: RouteLocationNormalized
): Promise<void> {
  const currentRouter = router ?? useRouter();
  const currentRoute = route ?? useRoute();

  if (jobId) {
    jobIdWrittenThisSession = true;
    await currentRouter.replace({
      ...currentRoute,
      query: {
        ...currentRoute.query,
        [TREATMENT_PLAN_JOB_ORIGIN]: jobId,
      },
    });
  } else {
    const query = { ...currentRoute.query };
    delete query[TREATMENT_PLAN_JOB_ORIGIN];
    await currentRouter.replace({
      ...currentRoute,
      query,
    });
  }
}
